const HAS_NON_WHITESPACE_REGEXP = /\S/;

export function utf8ByteLength(value: string) {
  let bytes = 0;

  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint < 0x80) bytes += 1;
    else if (codePoint < 0x800) bytes += 2;
    else if (codePoint < 0x10000) bytes += 3;
    else bytes += 4;
  }

  return bytes;
}

export function parseBoolean(value: string | undefined): boolean {
  return value === 'true';
}

/**
 * Checks if the given value is a non-empty string. Includes type-guard to narrow type.
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && HAS_NON_WHITESPACE_REGEXP.test(value);
}

export function trimOrNull(value: unknown): string | null {
  return isNonEmptyString(value) ? value.trim() : null;
}

export function trimOrUndefined(value: unknown): string | undefined {
  return isNonEmptyString(value) ? value.trim() : undefined;
}

export function trimAndLowercaseOrUndefined(value: unknown): string | undefined {
  return trimOrUndefined(value)?.toLowerCase();
}

/**
 * Normalizes first or last names for comparison: folds accents and case, removes apostrophes/periods,
 * and replaces remaining characters outside ASCII letters, numbers, and whitespace with spaces.
 * Returns null if nothing remains.
 */
export function normalizeNameForComparison(value: string | null | undefined): string | null {
  const normalized = value
    // NFD splits an accented letter into base + combining mark (ñ is one char -> "n" + U+0303) so the
    // next line can strip the mark; this matches a referral "Munoz" to a patient-typed "Muñoz".
    ?.normalize('NFD')
    .replace(new RegExp('\\p{Diacritic}', 'gu'), '')
    .toLowerCase()
    .replace(/['’.]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
  return normalized || null;
}

/**
 * Sanitizes an email address
 */
export function sanitizeEmail(email: string): string;
export function sanitizeEmail(email: null | undefined): undefined;
export function sanitizeEmail(email: string | null | undefined): string | undefined;
export function sanitizeEmail(email: string | null | undefined): string | undefined {
  return email?.trim().toLowerCase();
}

/**
 * Simple sanitization of a raw string that is supposed to be an NPI (National Provider Identifier).
 * Simply removes all non-digit characters, but does not validate the length nor checksum of the NPI.
 * Returns undefined if the NPI is not a non-empty string.
 */
export function sanitizeNpi(npi: unknown): string | undefined {
  if (!isNonEmptyString(npi)) {
    return undefined;
  }
  return npi.replace(/[^0-9]/g, '');
}

/**
 * Formats a price in cents (e.g. 1000) to "$10.00" (or "$10" if `roundToDollars` is true).
 */
export function formatPrice(costCents: number, roundToDollars?: boolean) {
  const dollars = (costCents / 100).toFixed(roundToDollars ? 0 : 2);
  return `$${dollars}`;
}

/**
 * Returns `singular` when `count` is exactly 1 and `plural` otherwise (including a missing count), e.g.
 * `pluralize(1, 'task')` is `'task'` and `pluralize(0, 'task')` is `'tasks'`. Pass `plural` for words that don't just
 * add an "s": `pluralize(count, 'specialty', 'specialties')`.
 */
export function pluralize(count: number | null | undefined, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}

/**
 * Prefixes the count to its pluralized noun, e.g. `pluralizeWithCount(1, 'task')` is `'1 task'`. Use `pluralize`
 * directly when the number needs its own formatting.
 */
export function pluralizeWithCount(count: number, singular: string, plural?: string): string {
  return `${count} ${pluralize(count, singular, plural)}`;
}
